// components/Charts/LineChart.jsx

import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { forwardRef, useImperativeHandle, useRef } from 'react';

// Eliminadas interfaces LineChartData y LineChartProps

const valueOnTopPlugin = {
    id: "valueOnTop",
    afterDatasetsDraw(chart) {
        // Deshabilitar siempre los valores encima de la línea
        return;

        if (chart.options.plugins?.valueOnTop?.display === false) return;
        const { ctx } = chart;

        chart.data.datasets.forEach((dataset, i) => {
            const meta = chart.getDatasetMeta(i);
            meta.data.forEach((element, index) => {
                const value = dataset.data[index];
                if (value === null || value === undefined) return;

                const { x, y } = element.tooltipPosition();

                ctx.save();
                ctx.font = "bold 12px sans-serif";
                ctx.fillStyle = dataset.borderColor || "#000";
                ctx.textAlign = "center";
                ctx.textBaseline = "bottom";

                ctx.strokeStyle = "white";
                ctx.lineWidth = 3;
                ctx.strokeText(value, x, y - 8);

                ctx.fillText(value, x, y - 8);
                ctx.restore();
            });
        });
    }
};

// Registrar Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    valueOnTopPlugin
);

// Eliminada la interfaz LineChartRef

const LineChart = forwardRef(({
    data,
    title = "Indicadores Línea",
    height = "500px",
    showTitle = true
}, ref) => {
    const canvasRef = useRef(null);
    const chartInstanceRef = useRef(null);

    useImperativeHandle(ref, () => ({
        getCanvas: () => canvasRef.current,
        toggleLegend: (show) => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.options.plugins.legend.display = show;
                chartInstanceRef.current.options.plugins.legend.position = 'bottom';
                chartInstanceRef.current.update();
            }
        },
        getLegendData: () => {
            if (!data || !data.datasets) return [];
            return data.datasets.map((dataset) => {
                const total = dataset.data.reduce((acc, curr) => acc + (curr || 0), 0);
                const unit = dataset.unit || '';
                return {
                    label: dataset.label,
                    value: `${total.toFixed(2)} ${unit}`,
                    color: dataset.borderColor
                };
            });
        }
    }));

    if (!data || !data.datasets || data.datasets.length === 0) {
        return (
            <div
                style={{
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#666',
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                    backgroundColor: '#f9f9f9',
                }}
            >
                <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ marginBottom: '16px', opacity: 0.5 }}
                >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                </svg>
                <p style={{ fontSize: '16px', fontWeight: 500 }}>
                    No hay datos de indicadores disponibles
                </p>
                <p style={{ fontSize: '14px', marginTop: '8px', opacity: 0.7 }}>
                    Por favor, verifica que existan reportes para este proyecto
                </p>
            </div>
        );
    }

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
                position: 'bottom',
                labels: {
                    boxWidth: 12,
                    padding: 15,
                    usePointStyle: true,
                    generateLabels: (chart) => {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset, i) => {
                            const total = dataset.data.reduce((acc, curr) => acc + (curr || 0), 0);
                            const unit = dataset.unit || '';

                            return {
                                text: `${dataset.label}: ${total.toFixed(2)} ${unit}`,
                                fillStyle: dataset.backgroundColor,
                                strokeStyle: dataset.borderColor,
                                lineWidth: dataset.borderWidth,
                                hidden: !chart.isDatasetVisible(i),
                                index: i
                            };
                        });
                    }
                }
            },
            title: {
                display: showTitle,
                text: title,
                font: { size: 16, weight: 700 }
            },
            tooltip: {
                backgroundColor: 'rgba(0,0,0,0.9)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#666',
                borderWidth: 1,
                padding: 10,
            },
            valueOnTop: false
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Valores",
                    font: {
                        size: 14,
                        weight: "bold"
                    }
                },
                grid: { color: 'rgba(0,0,0,0.1)' },
                ticks: {
                    padding: 12
                }
            },
            x: {
                title: {
                    display: true,
                    text: "Meses",
                    font: {
                        size: 14,
                        weight: "bold"
                    }
                },
                offset: true,
                grid: { display: false }
            }
        }
    };

    const baseHeight = parseInt(height.replace('px', '')) || 500;
    const legendItemHeight = 25; 
    const itemsPerRow = 2; 
    const legendRows = data ? Math.ceil(data.datasets.length / itemsPerRow) : 0;
    const dynamicLegendHeight = legendRows * legendItemHeight;
    const finalHeight = `${baseHeight + Math.max(0, dynamicLegendHeight - 50)}px`;

    return (
        <div style={{ height: finalHeight, width: "100%" }}>
            <Line
                data={data}
                options={options}
                ref={(instance) => {
                    if (instance) {
                        canvasRef.current = instance.canvas;
                        chartInstanceRef.current = instance;
                    }
                }}
            />
        </div>
    );
});

export default LineChart;